import React, { useEffect, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import LoadingCircle from 'components/ui/LoadingCircle';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import SearchField from 'components/v2/SearchField';
import { Menu } from '@headlessui/react';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import Pagination from 'components/v2/PaginationV2';
import moment from 'moment';
import useModal from 'hooks/useModal';
import { useDeleteReport, useGroups, useReports, useToggleFavoriteReport, useUpdateReport, } from 'hooks/unifiedview/useReports';
import Alert from 'components/ui/Alert';
import useDebounce from 'hooks/useDebounce';
import Tooltip from 'components/ui/Tooltip';
import { CheckIcon } from '@heroicons/react/20/solid';
import SessionStore from 'stores/SessionStore';
import { Table, TableFooter, TableRow, TableCell, TableBody, TableHeader, TableHead, SortableColumnHeader, } from 'components/ui/table';
import { useTranslation } from 'react-i18next';
import Favorite from './Favorite';
import RenameReportModal from './RenameReportModal';
import DeleteReportModal from './DeleteReportModal';
import Layout from './Layout';
import Breadcrumbs from './Breadcrumbs';
import { SIDEBAR_STATUSES, VIEWS } from './constants';
import { firstLetterTitlization } from '../../../utils/localization/utils';
const DEFAULT_REPORTS_PER_PAGE = 15;
export default function Reports() {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const { visible: renameModalVisible, show: showRenameModal, close: closeRenameModal, } = useModal();
    const { visible: confirmDeleteModalVisible, show: showConfirmDeleteModal, close: closeConfirmDeleteModal, } = useModal();
    const params = useParams();
    const location = useLocation();
    const { t } = useTranslation();
    const groupId = params.groupId || '';
    const favorites = location.pathname.includes('favorites');
    const isMyReports = location.pathname.includes('my-reports');
    const isGroups = location.pathname.includes('groups');
    const populatedFieldsSortable = favorites || isMyReports || isGroups;
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_REPORTS_PER_PAGE);
    const [selectedReport, setSelectedReport] = useState(null);
    const [sortKey, setSortKey] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    function toggleFavorite(report) {
        return function (e) {
            e === null || e === void 0 ? void 0 : e.preventDefault();
            toggleFavoriteMutation.mutate({
                id: report._id,
                isFavorite: !report.isFavorite,
            });
        };
    }
    function handleSearch(search) {
        setSearch(search);
    }
    function getView() {
        if (favorites) {
            return VIEWS.FAVORITES.name;
        }
        if (isMyReports) {
            return VIEWS.MY_REPORTS.name;
        }
        if (!groupId) {
            return VIEWS.ALL_REPORTS.name;
        }
        return VIEWS.GROUPS.name;
    }
    const filters = {
        groupId,
        favorites,
        search: debouncedSearch,
        page,
        perPage,
        sortKey,
        sortOrder,
        isMyReports,
        lean: !populatedFieldsSortable,
    };
    const { data, error, isLoading, isError, isSuccess } = useReports({
        filters,
    });
    const { mutation } = useUpdateReport({
        filters,
        onSuccess: handleSaveSuccess,
    });
    const { mutation: toggleFavoriteMutation } = useToggleFavoriteReport({
        filters,
        onSuccess: () => {
            checkPageOver(data.total - 1);
        },
    });
    const { mutation: deleteMutation } = useDeleteReport({
        filters,
        onSuccess: () => {
            handleDeleteSuccess();
            checkPageOver(data.total - 1);
        },
    });
    const { getGroupById } = useGroups();
    useEffect(() => {
        checkPageOver();
    }, [data]);
    const checkPageOver = (itemsCount) => {
        if (!itemsCount) {
            itemsCount = data === null || data === void 0 ? void 0 : data.total;
        }
        const totalPages = Math.ceil(itemsCount / perPage);
        if (page > totalPages) {
            setPage(totalPages > 0 ? totalPages : 1);
        }
    };
    function handleRename(report) {
        return function () {
            setSelectedReport(report);
            showRenameModal();
        };
    }
    function handleDelete(report) {
        deleteMutation.mutate(Object.assign(Object.assign({}, report), { id: report._id }));
    }
    function handleConfirmDelete(report) {
        return function () {
            setSelectedReport(report);
            showConfirmDeleteModal();
        };
    }
    function handleDeleteSuccess() {
        closeConfirmDeleteModal();
    }
    function handleSave(report) {
        mutation.mutate(report);
    }
    function handlePageClick(page) {
        setPage(parseInt(page, 10));
    }
    function handleSaveSuccess() {
        closeRenameModal();
    }
    const handleSort = (sortConfig) => {
        setSortKey(sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.key);
        setSortOrder(sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.order);
    };
    const getGroupFullName = (group) => {
        if (!group) {
            return '';
        }
        const parentGroup = getGroupById(group.parent);
        return parentGroup ? `${parentGroup.name} - ${group.name}` : group.name;
    };
    const formatUsername = (user) => {
        if (!user)
            return '';
        return `${user.first_name} ${user.last_name}`;
    };
    function renderReports() {
        var _a, _b;
        return (React.createElement(React.Fragment, null,
            React.createElement(DeleteReportModal, { report: selectedReport, isOpen: confirmDeleteModalVisible, onClose: closeConfirmDeleteModal, onConfirm: handleDelete }),
            React.createElement(RenameReportModal, { report: selectedReport, isOpen: renameModalVisible, onClose: closeRenameModal, onSave: handleSave }),
            React.createElement(Breadcrumbs, { groupId: groupId }),
            React.createElement(SearchField, { dataTestId: "SearchField-fb053362", className: "mb-4", onChange: handleSearch, value: search }),
            React.createElement("div", { "data-testid": "div-e877f3db", className: "table-wrapper overflow-auto relative", style: { maxHeight: 'calc(100dvh - 230px)' } },
                React.createElement(Table, { dataTestId: "Table-46c76356", className: "w-full" },
                    React.createElement(TableHeader, { dataTestId: "TableHeader-f17bd990", className: "sticky top-0 bg-gray-50", onSortChange: handleSort },
                        React.createElement(TableRow, { dataTestId: "TableRow-45709afe" },
                            React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-034720f5", sortKey: "name" }, t('NAME')),
                            React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-7ca8ef02", className: "" }, t('DESCRIPTION')),
                            React.createElement(TableHead, { dataTestId: "SortableColumnHeader-7ca8ef03" }, t('FILTER_TYPE')),
                            populatedFieldsSortable ? (React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-0159e5bd", sortKey: "groupId.name", className: "w-32" }, firstLetterTitlization(t('GROUP', { count: 1 })))) : (React.createElement(TableHead, { dataTestId: "TableHead-e0446590", className: "w-32" }, firstLetterTitlization(t('GROUP', { count: 1 })))),
                            populatedFieldsSortable ? (React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-51475ab4", sortKey: "creator_id.optimized_name", className: "w-32" }, t('CREATED_BY'))) : (React.createElement(TableHead, { dataTestId: "TableHead-f1960b0d", className: "w-32" }, t('CREATED_BY'))),
                            React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-d4700c84", sortKey: "created_at", className: "w-32" }, t('CREATED_AT')),
                            populatedFieldsSortable ? (React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-0ac09024", sortKey: "update_author.optimized_name", className: "w-32" }, t('UPDATED_BY'))) : (React.createElement(TableHead, { dataTestId: "TableHead-9cc2bf30", className: "w-32" }, t('UPDATED_BY'))),
                            React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-184dc96a", sortKey: "modified_at", className: "w-32" }, t('UPDATED_AT')),
                            React.createElement(TableHead, { dataTestId: "TableHead-39eea8c0", sortKey: "shared", className: "w-20" },
                                t('PUBLIC'),
                                "?"),
                            React.createElement(SortableColumnHeader, { dataTestId: "SortableColumnHeader-a1bbda62", className: "text-right w-12" },
                                React.createElement("span", { "data-testid": "span-9cd069aa", className: "sr-only" }, t('ACTIONS'))))),
                    React.createElement(TableBody, { dataTestId: "TableBody-b33a54be" },
                        isLoading && (React.createElement(TableRow, { dataTestId: "TableRow-e570ef23" },
                            React.createElement(TableCell, { dataTestId: "TableCell-934406f2", colSpan: "100%" },
                                React.createElement("span", { "data-testid": "span-2537e390", className: "sr-only" },
                                    t('LOADING'),
                                    "..."),
                                React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-4a7af0e9" })))),
                        !((_a = data === null || data === void 0 ? void 0 : data.items) === null || _a === void 0 ? void 0 : _a.length) && !isLoading ? (React.createElement(TableRow, { dataTestId: "TableRow-9e9d117d" },
                            React.createElement(TableCell, { dataTestId: "TableCell-af6fb912", colSpan: "100%", className: "text-center" },
                                React.createElement("span", { "data-testid": "span-23822ded", className: "" }, t('NO_REPORTS_FOUND'))))) : ((_b = data === null || data === void 0 ? void 0 : data.items) === null || _b === void 0 ? void 0 : _b.map((report, index) => (React.createElement(TableRow, { dataTestId: `TableRow-0e281ebb-${index}`, key: report._id },
                            React.createElement(TableCell, { dataTestId: `TableCell-04570e2c-${index}`, className: "w-64" }, report.name && (React.createElement(Tooltip, { dataTestId: `Tooltip-b410936e-${index}`, id: report.name, content: report.name, tooltipClassName: "w-auto" },
                                React.createElement("p", { "data-testid": `p-025dc39e-${index}`, className: "line-clamp-2 w-64 whitespace-normal" },
                                    React.createElement(Link, { to: `/unifiedview/reports/${report._id}` }, report.name))))),
                            React.createElement(TableCell, { dataTestId: `TableCell-f6c6735a-${index}`, className: "" }, report.description && (React.createElement(Tooltip, { dataTestId: `Tooltip-999cf0a1-${index}`, id: report._id, content: report.description, tooltipClassName: "w-auto" },
                                React.createElement("p", { "data-testid": `p-8a449fc1-${index}`, className: "line-clamp-2 w-64 whitespace-normal" }, report.description)))),
                            React.createElement(TableCell, { dataTestId: `TableCell-filter-type-${index}` }, report.filterType),
                            React.createElement(TableCell, { dataTestId: `TableCell-2efe77a0-${index}`, className: "w-32" },
                                React.createElement(Tooltip, { dataTestId: `Tooltip-0610e973-${index}`, id: report._id, content: getGroupFullName(report.groupId), tooltipClassName: "w-auto" }, getGroupFullName(report.groupId))),
                            React.createElement(TableCell, { dataTestId: `TableCell-989a16f5-${index}`, className: "w-32", title: report.createdBy },
                                React.createElement(Tooltip, { dataTestId: `Tooltip-265a399c-${index}`, id: report._id, content: formatUsername(report.creator_id), tooltipClassName: "w-auto" }, formatUsername(report.creator_id))),
                            React.createElement(TableCell, { dataTestId: `TableCell-7bbe8080-${index}`, className: "w-16" }, report.created_at
                                ? moment(report.created_at).format('MM/DD/YYYY')
                                : ''),
                            React.createElement(TableCell, { dataTestId: `TableCell-5a8cc57c-${index}`, className: "w-32 ", title: report.updatedBy },
                                React.createElement(Tooltip, { dataTestId: `Tooltip-dc71f4f5-${index}`, id: report._id, content: formatUsername(report.update_author), tooltipClassName: "w-auto" }, formatUsername(report.update_author))),
                            React.createElement(TableCell, { dataTestId: `TableCell-2f53360e-${index}`, className: "w-16" }, report.modified_at
                                ? moment(report.modified_at).format('MM/DD/YYYY')
                                : null),
                            React.createElement(TableCell, { dataTestId: `TableCell-08f19eee-${index}`, className: "w-8" }, report.shared && (React.createElement(CheckIcon, { className: "text-green-cm h-5 w-5" }))),
                            React.createElement(TableCell, { dataTestId: `TableCell-0dd60a77-${index}`, className: "text-right w-20" },
                                React.createElement("span", { "data-testid": `span-d1d1d754-${index}`, className: "sr-only" }, "Menu"),
                                React.createElement("div", { "data-testid": `div-e07d1d7e-${index}`, className: "flex items-center space-x-2 justify-end" },
                                    React.createElement(Favorite, { selected: report.isFavorite, onToggle: toggleFavorite(report) }),
                                    React.createElement(DropdownMenu, { dataTestId: `DropdownMenu-92ccbbf7-${index}`, useIcon: true },
                                        React.createElement(Menu.Item, { "data-testid": `MenuItem-154c0ab1-${index}`, key: "delete_report" },
                                            React.createElement(DropdownItem, { dataTestId: "deleteReportButton", onClick: handleConfirmDelete(report), disabled: !SessionStore.isAbleToManageUVReport(report), disabledText: "You do not have permission to delete this report", disabledTextPosition: "left" },
                                                React.createElement("span", { "data-testid": `span-5eb6ba44-${index}` }, t('DELETE')))),
                                        React.createElement(Menu.Item, { "data-testid": `MenuItem-b9e6c469-${index}`, key: "rename_report" },
                                            React.createElement(DropdownItem, { dataTestId: "renameReportButton", onClick: handleRename(report), disabled: !SessionStore.isAbleToManageUVReport(report), disabledText: "You do not have permission to rename this report", disabledTextPosition: "left" },
                                                React.createElement("span", { "data-testid": `span-5a780026-${index}` }, t('RENAME')))))))))))),
                    React.createElement(TableFooter, { dataTestId: "TableFooter-dff19e13", className: "sticky bottom-0 bg-gray-50" }, (data === null || data === void 0 ? void 0 : data.total) > perPage && (React.createElement(TableRow, { dataTestId: "TableRow-c9b23b38", className: "border-0" },
                        React.createElement(TableCell, { dataTestId: "TableCell-93b802ce", colSpan: "100%" },
                            React.createElement("span", { "data-testid": "span-b30993d4", className: "sr-only" }, "Pagination"),
                            React.createElement(Pagination, { dataTestId: "Pagination-2d73a4cf", page: page, perPage: perPage, total: data === null || data === void 0 ? void 0 : data.total, onPageClick: handlePageClick, showCount: true })))))))));
    }
    return (React.createElement(Layout, { groupId: groupId, activeTab: getView(), sidebarDefaultState: SIDEBAR_STATUSES.EXPANDED },
        React.createElement("div", { "data-testid": "div-5b3b1d0f", className: "px-4 sm:px-6 lg:px-8" },
            isError && (React.createElement(Alert, { dataTestId: "Alert-8cd6f4cf", type: "error", className: "my-4" }, error)),
            renderReports())));
}
